const MAX_BOOT_TRACE_EVENTS = 96;
export const SLOW_BOOT_THRESHOLD_MS = 8_000;

export type BootBlocker =
  | 'app-shell'
  | 'doc-cache-local'
  | 'feature-entitlement-local'
  | 'folder-cache-local'
  | 'folder-cache-populated'
  | 'local-doc-storage'
  | 'permission-cache-local'
  | 'permission-cache-populated'
  | 'root-doc'
  | 'workspace-list'
  | 'workspace-open'
  | null;

export type BootTraceData = Record<
  string,
  boolean | number | string | null | undefined
>;

export interface BootTraceEvent {
  readonly atMs: number;
  readonly data?: BootTraceData;
  readonly name: string;
}

export interface BootTraceSnapshot {
  readonly blocker: BootBlocker;
  readonly completed: boolean;
  readonly durationMs: number;
  readonly events: readonly BootTraceEvent[];
  readonly scheduling?: Readonly<BootSchedulingSummary>;
  readonly navigation?: Readonly<BootNavigationSummary>;
  readonly network?: Readonly<BootNetworkSummary>;
  readonly slow: boolean;
}

export interface BootSchedulingSummary {
  /** Number of event-loop delay samples collected during boot. */
  samples: number;
  /** Largest delay beyond the expected 50 ms sampling interval. */
  maxDelayMs: number;
  /** 95th percentile event-loop delay. */
  p95DelayMs: number;
  /** Samples delayed by at least 100 ms. */
  over100Ms: number;
  /** Samples delayed by at least 250 ms. */
  over250Ms: number;
  /** Sum of all sampled event-loop delay. */
  totalDelayMs: number;
  /** Browser Long Tasks observed during boot. */
  longTasks: number;
  longTaskMaxMs: number;
  longTaskTotalMs: number;
}

export interface BootNavigationSummary {
  /** User-perceived time from navigation start until StreamLain signals ready. */
  appReadyMs: number;
  /** Time spent until the HTML/bundle response completed. Includes dev compilation. */
  responseEndMs: number;
}

export interface BootNetworkSummary {
  api: number;
  graphql: number;
  health: number;
  other: number;
  realtime: number;
  total: number;
}

export class BootTraceRecorder {
  private blocker: BootBlocker = 'app-shell';
  private completed = false;
  private readonly events: BootTraceEvent[] = [];
  private readonly startedAt: number;

  constructor(
    private readonly now: () => number = () => performance.now(),
    private readonly mark: (name: string) => void = name =>
      performance.mark(`streamlain.boot.${name}`)
  ) {
    this.startedAt = this.now();
    this.record('boot-started');
  }

  record(name: string, data?: BootTraceData): void {
    const event: BootTraceEvent = {
      atMs: Math.round((this.now() - this.startedAt) * 10) / 10,
      ...(data ? { data } : {}),
      name,
    };
    const previous = this.events.at(-1);
    if (
      previous?.name === event.name &&
      JSON.stringify(previous.data) === JSON.stringify(event.data)
    ) {
      return;
    }
    this.events.push(event);
    if (this.events.length > MAX_BOOT_TRACE_EVENTS) {
      this.events.shift();
    }
    try {
      this.mark(name);
    } catch {
      // Performance marks are diagnostic only and must never affect boot.
    }
  }

  setBlocker(blocker: BootBlocker, data?: BootTraceData): void {
    if (this.completed || this.blocker === blocker) {
      return;
    }
    this.blocker = blocker;
    this.record('blocker-changed', { blocker: blocker ?? 'none', ...data });
  }

  complete(data?: BootTraceData): void {
    if (this.completed) {
      return;
    }
    this.completed = true;
    this.blocker = null;
    this.record('boot-completed', data);
  }

  snapshot(): BootTraceSnapshot {
    const durationMs = Math.round((this.now() - this.startedAt) * 10) / 10;
    return {
      blocker: this.blocker,
      completed: this.completed,
      durationMs,
      events: this.events.map(event => ({
        ...event,
        ...(event.data ? { data: { ...event.data } } : {}),
      })),
      slow: !this.completed && durationMs >= SLOW_BOOT_THRESHOLD_MS,
    };
  }
}

const bootTrace = new BootTraceRecorder();
let slowBootTimer: number | undefined;
const bootNetworkSummary: BootNetworkSummary = {
  api: 0,
  graphql: 0,
  health: 0,
  other: 0,
  realtime: 0,
  total: 0,
};
let resourceObserver: PerformanceObserver | undefined;
let longTaskObserver: PerformanceObserver | undefined;
let schedulerTimer: number | undefined;
let schedulerExpectedAt = 0;
const schedulerDelays: number[] = [];
const longTaskDurations: number[] = [];
let appReadyAtNavigationMs: number | undefined;
const SCHEDULER_INTERVAL_MS = 50;
const MAX_SCHEDULER_SAMPLES = 2_048;
const MAX_LONG_TASK_SAMPLES = 512;

function rounded(value: number): number {
  return Math.round(value * 10) / 10;
}

export function summarizeBootScheduling(
  delays: readonly number[],
  longTasks: readonly number[]
): BootSchedulingSummary {
  const sortedDelays = [...delays].sort((a, b) => a - b);
  const percentileIndex =
    sortedDelays.length === 0
      ? -1
      : Math.min(
          sortedDelays.length - 1,
          Math.ceil(sortedDelays.length * 0.95) - 1
        );
  return {
    samples: delays.length,
    maxDelayMs: rounded(Math.max(0, ...delays)),
    p95DelayMs:
      percentileIndex === -1 ? 0 : rounded(sortedDelays[percentileIndex]),
    over100Ms: delays.filter(delay => delay >= 100).length,
    over250Ms: delays.filter(delay => delay >= 250).length,
    totalDelayMs: rounded(delays.reduce((total, delay) => total + delay, 0)),
    longTasks: longTasks.length,
    longTaskMaxMs: rounded(Math.max(0, ...longTasks)),
    longTaskTotalMs: rounded(
      longTasks.reduce((total, duration) => total + duration, 0)
    ),
  };
}

function observeBootRequest(entry: PerformanceEntry): void {
  let pathname = '';
  try {
    pathname = new URL(entry.name, location.origin).pathname;
  } catch {
    // Unparseable resource names remain in the anonymous "other" bucket.
  }
  bootNetworkSummary.total++;
  if (pathname === '/graphql') {
    bootNetworkSummary.graphql++;
  } else if (pathname === '/api/health') {
    bootNetworkSummary.health++;
  } else if (pathname.startsWith('/socket.io')) {
    bootNetworkSummary.realtime++;
  } else if (pathname.startsWith('/api/')) {
    bootNetworkSummary.api++;
  } else {
    bootNetworkSummary.other++;
  }
}

function bootSnapshot(): BootTraceSnapshot {
  const navigation = performance.getEntriesByType('navigation')[0] as
    | PerformanceNavigationTiming
    | undefined;
  return {
    ...bootTrace.snapshot(),
    ...(navigation
      ? {
          navigation: {
            appReadyMs:
              appReadyAtNavigationMs ?? Math.round(performance.now() * 10) / 10,
            responseEndMs: Math.round(navigation.responseEnd * 10) / 10,
          },
        }
      : {}),
    network: { ...bootNetworkSummary },
    scheduling: summarizeBootScheduling(schedulerDelays, longTaskDurations),
  };
}

declare global {
  interface Window {
    /** Safe boot diagnostics: timings and state only, no IDs or user content. */
    __streamlainBootTrace?: () => BootTraceSnapshot;
  }
}

export function installBootTrace(): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.__streamlainBootTrace = bootSnapshot;
  if (typeof PerformanceObserver !== 'undefined' && !resourceObserver) {
    resourceObserver = new PerformanceObserver(list => {
      for (const entry of list.getEntries()) {
        observeBootRequest(entry);
      }
    });
    resourceObserver.observe({ entryTypes: ['resource'] });
  }
  if (typeof PerformanceObserver !== 'undefined' && !longTaskObserver) {
    try {
      longTaskObserver = new PerformanceObserver(list => {
        for (const entry of list.getEntries()) {
          longTaskDurations.push(entry.duration);
          if (longTaskDurations.length > MAX_LONG_TASK_SAMPLES) {
            longTaskDurations.shift();
          }
        }
      });
      longTaskObserver.observe({ entryTypes: ['longtask'] });
    } catch {
      longTaskObserver = undefined;
    }
  }
  if (schedulerTimer === undefined) {
    schedulerExpectedAt = performance.now() + SCHEDULER_INTERVAL_MS;
    schedulerTimer = window.setInterval(() => {
      const now = performance.now();
      schedulerDelays.push(Math.max(0, now - schedulerExpectedAt));
      if (schedulerDelays.length > MAX_SCHEDULER_SAMPLES) {
        schedulerDelays.shift();
      }
      schedulerExpectedAt = now + SCHEDULER_INTERVAL_MS;
    }, SCHEDULER_INTERVAL_MS);
  }
  slowBootTimer ??= window.setTimeout(() => {
    const snapshot = bootSnapshot();
    if (!snapshot.completed) {
      bootTrace.record('slow-boot-detected', {
        blocker: snapshot.blocker ?? 'unknown',
        durationMs: snapshot.durationMs,
      });
      window.dispatchEvent(
        new CustomEvent('streamlain:slow-boot', {
          detail: bootSnapshot(),
        })
      );
    }
  }, SLOW_BOOT_THRESHOLD_MS);
}

export function recordBootEvent(name: string, data?: BootTraceData): void {
  bootTrace.record(name, data);
}

export function setBootBlocker(
  blocker: BootBlocker,
  data?: BootTraceData
): void {
  bootTrace.setBlocker(blocker, data);
}

export function completeBootTrace(data?: BootTraceData): void {
  if (bootTrace.snapshot().completed) {
    return;
  }
  appReadyAtNavigationMs = Math.round(performance.now() * 10) / 10;
  bootTrace.complete(data);
  resourceObserver?.disconnect();
  resourceObserver = undefined;
  longTaskObserver?.disconnect();
  longTaskObserver = undefined;
  if (schedulerTimer !== undefined) {
    window.clearInterval(schedulerTimer);
    schedulerTimer = undefined;
  }
  if (slowBootTimer !== undefined) {
    window.clearTimeout(slowBootTimer);
    slowBootTimer = undefined;
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('streamlain:boot-complete', {
        detail: bootSnapshot(),
      })
    );
  }
}

installBootTrace();
recordBootEvent('browser-bootstrap-loaded');
